// src/features/tabs/logic.ts
// Pure operations on the workspace layout. Kept free of React and of the store
// so the tree behaviour — splitting, moving a tab between panes, collapsing an
// emptied pane — is testable on its own.

import { buildBreadcrumbs } from '@/features/layout/logic';
import type {
  ContentAlign,
  ContentWidth,
  PaneNode,
  SplitNode,
  TabGroupNode,
  WorkspaceLayoutState,
  WorkspaceTab,
} from './types';

const STORAGE_KEY = 'corde.workspace.layout';
/** Panes kept mounted at once. Beyond this the least recently seen is dropped. */
export const MAX_LIVE_TABS = 8;
const MIN_RATIO = 0.15;
const MAX_RATIO = 0.85;

let idCounter = 0;
function nextId(prefix: string): string {
  idCounter += 1;
  return `${prefix}-${Date.now().toString(36)}-${idCounter}`;
}

export function createTab(path: string, title: string): WorkspaceTab {
  return { id: nextId('tab'), path, title, pinned: false };
}

export function createGroup(tabIds: string[] = []): TabGroupNode {
  return {
    kind: 'group',
    id: nextId('group'),
    tabIds,
    activeTabId: tabIds[0] ?? null,
    width: 'full',
    align: 'center',
  };
}

/** A tab's display name. The same labels as the breadcrumbs, deliberately. */
export function resolveTitle(path: string, t: (key: string) => string): string {
  const crumbs = buildBreadcrumbs(path, t);
  return crumbs[crumbs.length - 1]?.label ?? path;
}

// --- tree walking ------------------------------------------------------------

export function findGroup(node: PaneNode, groupId: string): TabGroupNode | null {
  if (node.kind === 'group') return node.id === groupId ? node : null;
  return findGroup(node.children[0], groupId) ?? findGroup(node.children[1], groupId);
}

export function allGroups(node: PaneNode): TabGroupNode[] {
  if (node.kind === 'group') return [node];
  return [...allGroups(node.children[0]), ...allGroups(node.children[1])];
}

export function groupHoldingTab(node: PaneNode, tabId: string): TabGroupNode | null {
  return allGroups(node).find((g) => g.tabIds.includes(tabId)) ?? null;
}

/** Replaces one node by id, returning a new tree. */
function replaceNode(node: PaneNode, targetId: string, next: PaneNode): PaneNode {
  if (node.id === targetId) return next;
  if (node.kind === 'group') return node;
  return {
    ...node,
    children: [
      replaceNode(node.children[0], targetId, next),
      replaceNode(node.children[1], targetId, next),
    ],
  };
}

/**
 * Removes a node and lifts its sibling into the parent's place — the reason an
 * emptied pane hands its space back instead of leaving a blank rectangle.
 */
function removeNode(node: PaneNode, targetId: string): PaneNode | null {
  if (node.id === targetId) return null;
  if (node.kind === 'group') return node;
  const left = removeNode(node.children[0], targetId);
  const right = removeNode(node.children[1], targetId);
  if (left === null) return right;
  if (right === null) return left;
  return { ...node, children: [left, right] };
}

// --- operations --------------------------------------------------------------

export function setRatio(root: PaneNode, splitId: string, ratio: number): PaneNode {
  const clamped = Math.min(MAX_RATIO, Math.max(MIN_RATIO, ratio));
  const walk = (node: PaneNode): PaneNode => {
    if (node.kind === 'group') return node;
    if (node.id === splitId) return { ...node, ratio: clamped };
    return { ...node, children: [walk(node.children[0]), walk(node.children[1])] };
  };
  return walk(root);
}

/**
 * Splits a group, putting `movingTabId` alone in the new pane. `before` decides
 * which side the new pane lands on, so dropping on the left edge of a pane puts
 * the tab on the left.
 */
export function splitGroup(
  root: PaneNode,
  groupId: string,
  direction: 'row' | 'column',
  movingTabId: string,
  before: boolean,
): { root: PaneNode; newGroupId: string } {
  const source = findGroup(root, groupId);
  if (!source) return { root, newGroupId: groupId };

  const remaining = source.tabIds.filter((id) => id !== movingTabId);
  const kept: TabGroupNode = {
    ...source,
    tabIds: remaining,
    activeTabId: remaining.includes(source.activeTabId ?? '')
      ? source.activeTabId
      : (remaining[0] ?? null),
  };
  const created: TabGroupNode = {
    ...createGroup([movingTabId]),
    width: source.width,
    align: source.align,
  };

  // Splitting off the only tab would leave an empty pane beside a full one.
  if (remaining.length === 0) return { root, newGroupId: groupId };

  const split: SplitNode = {
    kind: 'split',
    id: nextId('split'),
    direction,
    children: before ? [created, kept] : [kept, created],
    ratio: 0.5,
  };
  return { root: replaceNode(root, groupId, split), newGroupId: created.id };
}

/** Moves a tab into a group at a position, from wherever it currently is. */
export function moveTab(
  root: PaneNode,
  tabId: string,
  toGroupId: string,
  index: number,
): PaneNode {
  const from = groupHoldingTab(root, tabId);
  if (!from) return root;

  let next = root;
  if (from.id === toGroupId) {
    const without = from.tabIds.filter((id) => id !== tabId);
    const at = Math.max(0, Math.min(without.length, index));
    without.splice(at, 0, tabId);
    return replaceNode(next, from.id, { ...from, tabIds: without, activeTabId: tabId });
  }

  const shrunk = from.tabIds.filter((id) => id !== tabId);
  next = replaceNode(next, from.id, {
    ...from,
    tabIds: shrunk,
    activeTabId: shrunk.includes(from.activeTabId ?? '')
      ? from.activeTabId
      : (shrunk[0] ?? null),
  });

  const target = findGroup(next, toGroupId);
  if (!target) return root;
  const ids = [...target.tabIds];
  ids.splice(Math.max(0, Math.min(ids.length, index)), 0, tabId);
  next = replaceNode(next, toGroupId, { ...target, tabIds: ids, activeTabId: tabId });

  // A group left with nothing gives its space back to its sibling.
  const emptied = findGroup(next, from.id);
  if (emptied && emptied.tabIds.length === 0 && allGroups(next).length > 1) {
    next = removeNode(next, from.id) ?? next;
  }
  return next;
}

export function closeTab(root: PaneNode, tabId: string): PaneNode {
  const group = groupHoldingTab(root, tabId);
  if (!group) return root;
  const ids = group.tabIds.filter((id) => id !== tabId);
  let next = replaceNode(root, group.id, {
    ...group,
    tabIds: ids,
    activeTabId: ids.includes(group.activeTabId ?? '')
      ? group.activeTabId
      : (ids[ids.length - 1] ?? null),
  });
  if (ids.length === 0 && allGroups(next).length > 1) {
    next = removeNode(next, group.id) ?? next;
  }
  return next;
}

export function setGroupPresentation(
  root: PaneNode,
  groupId: string,
  patch: { width?: ContentWidth; align?: ContentAlign },
): PaneNode {
  const group = findGroup(root, groupId);
  if (!group) return root;
  return replaceNode(root, groupId, { ...group, ...patch });
}

// --- persistence -------------------------------------------------------------

export function emptyLayout(): WorkspaceLayoutState {
  const group = createGroup([]);
  return { version: 1, tabs: [], root: group, focusedGroupId: group.id };
}

/**
 * Rejects a stored layout that does not hang together — a tab referenced by no
 * pane, or a pane referencing a tab that is gone — rather than rendering a
 * workspace with holes in it.
 */
function isCoherent(state: WorkspaceLayoutState): boolean {
  if (state.version !== 1 || !state.root || !Array.isArray(state.tabs)) return false;
  const groups = allGroups(state.root);
  if (groups.length === 0) return false;
  const referenced = new Set(groups.flatMap((g) => g.tabIds));
  const known = new Set(state.tabs.map((tb) => tb.id));
  for (const id of referenced) if (!known.has(id)) return false;
  for (const id of known) if (!referenced.has(id)) return false;
  return groups.some((g) => g.id === state.focusedGroupId);
}

export function loadLayout(): WorkspaceLayoutState | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as WorkspaceLayoutState;
    return isCoherent(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

export function saveLayout(state: WorkspaceLayoutState): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Layout is a convenience; a full quota must not break navigation.
  }
}
